import * as React from 'react'
import { useForm } from 'react-hook-form'

import { AuthHeading } from '../../components/authentication/auth-heading'
import { Button } from '../../components/button'
import { FormGroup } from '../../components/form-group'
import { Input } from '../../components/input'
import { Intent } from '../../config/intent'
import { authService } from '../../lib/auth-service'
import { Notify } from '../../lib/helpers'
import { Validation } from '../../lib/validation'

export const TestId = {
    FORM: 'reset-password-confirm-form',
}

export const I18n = {
    HEADING: 'Enter 2FA verification code',
    CODE_LABEL: 'Verification code*',
    PASSWORD_LABEL: 'New password*',
    SUBMIT: 'Reset password',
    PASSWORD_TOO_SHORT: 'Password not long enought',
}

interface ConfirmFormValues {
    code: string
    password: string
}

interface ConfirmStepProps {
    email: string
    subHeading: string
    onComplete: (data: ConfirmFormValues) => void
}

export function ConfirmStep({
    email,
    subHeading,
    onComplete,
}: ConfirmStepProps) {
    const [formError, setFormError] = React.useState('')
    const [loading, setLoading] = React.useState(false)
    const [showPassword, setShowPassword] = React.useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
    } = useForm<ConfirmFormValues>()

    function handleError(err: any) {
        if (err.code === 'CodeMismatchException') {
            setError('code', { message: 'Incorrect verification code' })
        } else if (
            err.message ===
            'Password does not conform to policy: Password not long enough'
        ) {
            setError('password', { message: I18n.PASSWORD_TOO_SHORT })
        } else {
            setFormError(err.message)
        }
    }

    async function onSubmit(data: ConfirmFormValues) {
        setLoading(true)
        setFormError('')

        try {
            await authService.confirmPassword({
                email,
                code: data.code,
                password: data.password,
            })
            onComplete(data)
            Notify('success', 'Yippee!', 'Your password has been updated')
        } catch (err) {
            handleError(err)
            setLoading(false)
            Notify('danger', 'Hoopsies!', 'Your password could not be updated')
        }
    }

    return (
        <React.Fragment>
            <AuthHeading heading={I18n.HEADING} subHeading={subHeading} />
            <form onSubmit={handleSubmit(onSubmit)} data-testid={TestId.FORM}>
                <FormGroup
                    label={I18n.CODE_LABEL}
                    htmlFor='code'
                    intent={
                        errors.code?.message ? Intent.DANGER : Intent.DEFAULT
                    }
                    message={errors.code?.message}>
                    <Input
                        intent={
                            errors.code?.message
                                ? Intent.DANGER
                                : Intent.DEFAULT
                        }
                        id='code'
                        {...register('code', Validation.verificationCode)}
                    />
                </FormGroup>
                <FormGroup
                    label={I18n.PASSWORD_LABEL}
                    htmlFor='password'
                    intent={
                        errors.password?.message
                            ? Intent.DANGER
                            : Intent.DEFAULT
                    }
                    message={errors.password?.message}>
                    <Input
                        type={showPassword ? 'text' : 'password'}
                        intent={
                            errors.password?.message
                                ? Intent.DANGER
                                : Intent.DEFAULT
                        }
                        rightElement={
                            <Button
                                minimal={true}
                                onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? 'Hide' : 'Show'}
                            </Button>
                        }
                        id='password'
                        {...register('password', Validation.password)}
                    />
                </FormGroup>
                <Button
                    loading={loading}
                    disabled={loading}
                    fill={true}
                    type='submit'
                    size='large'
                    className='mb-4'>
                    {I18n.SUBMIT}
                </Button>
                {formError && (
                    <p className='text-center text-danger'>{formError}</p>
                )}
            </form>
        </React.Fragment>
    )
}
